// ════════════════════════════════════════════════════════════════════════════
//  ManDana — where a record stands, stored
//  ---------------------------------------------------------------------------
//  One flat document, `md:recordStatus`, mapping a record's uid to the id of
//  the status it is standing on. That is the whole module. What the word MEANS
//  — which workflow it belongs to, what absence resolves to, what the tick has
//  to do — is md-project.ts's and md-record.ts's business; this only knows
//  where the word is kept and how it travels.
//
//  ── WHY IT IS ITS OWN DOCUMENT, AND FLAT ──────────────────────────────────
//  NOT IN THE DAY. `md:events:{day}` is the one shape in this app that merges
//  item by item and carries the sync deletion trap md-board.ts's header
//  describes. Moving a card on a board must never be able to revise what
//  somebody wrote on a Tuesday, so the word stays out of there — the rule
//  Phase 5 set down and the shipped build broke by stamping `status` onto the
//  day's own rows.
//
//  NOT BESIDE A PROJECT. Every record has a status, including the ones that
//  belong to nothing at all (Marwan, 2026-08-18). A map keyed by project has
//  nowhere to put a thought nobody has filed, which is why the door that used
//  to own this refused one outright. Everything is somewhere; a store that
//  cannot express that is the wrong store.
//
//  FLAT, AND KEYED BY RECORD, because the day's completions map is already
//  exactly this shape and the sync layer already merges it per key. Two
//  devices moving two different records is not a conflict, and two devices
//  moving the SAME record is one key with a last writer — the same bargain the
//  tick has always made. A per-project document would have turned the first
//  case into a conflict for no reason.
//
//  ── ABSENCE, AND THE WORD THAT SHIPPED ────────────────────────────────────
//  No entry means nothing has been said, which resolves to the workflow's
//  Inbox one floor up. Nothing is ever backfilled to make that true.
//
//  But the shipped build stored a record's status ON THE ROW, and those
//  records exist on real devices. md-project.ts reads that as a fallback, and
//  the one consequence lands here: clearing a record whose row still carries a
//  word cannot be done by saying nothing, because the row's word would rise
//  again. It is written out as an explicit Inbox instead. `mdRecordStatusSet`
//  therefore stores whatever it is given and clears only on null — the
//  judgement about which of those to ask for belongs to the caller that knows
//  what is underneath.
// ════════════════════════════════════════════════════════════════════════════

(function (g: any) {
  'use strict';

  const KEY = 'md:recordStatus';
  const DOC = 'record-status';

  function readMap(): Record<string, string> {
    const raw = g.mdRead(KEY, null);
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
    const out: Record<string, string> = {};
    for (const uid of Object.keys(raw)) {
      const word = (raw as Record<string, unknown>)[uid];
      if (uid && typeof word === 'string' && word) out[uid] = word;
    }
    return out;
  }

  /** Every word every record is wearing. Callers that need to count or to
      rewrite in bulk read this once rather than asking per record. */
  function mdRecordStatusAll(): Record<string, string> {
    return readMap();
  }

  /** What one record has been moved to, or null for one that has never been
      moved. This is the STORED word and deliberately not the resolved one —
      resolution needs a workflow, and a workflow is not this module's to know. */
  function mdRecordStatusStored(uid: unknown): string | null {
    if (typeof uid !== 'string' || !uid) return null;
    return readMap()[uid] || null;
  }

  /** Put the map back, and forget the document when the last word leaves it.
      An account that tried a status and changed its mind leaves nothing
      behind — the same bargain md-workflow.ts makes for its assignments. */
  function writeMap(map: Record<string, string>): boolean {
    if (Object.keys(map).length) return g.mdWrite(KEY, map);
    if (g.mdRead(KEY, null) == null) return true;
    return g.mdRemove(KEY);
  }

  /** Move one record, or clear it with null. Returns false only when the write
      itself failed, so a caller can tell "nothing to do" from "the device is
      full" by asking mdStorageLastError. */
  function mdRecordStatusSet(uid: unknown, status: unknown): boolean {
    if (typeof uid !== 'string' || !uid) return false;
    const map = readMap();
    if (status == null || status === '') {
      if (!(uid in map)) return true;
      delete map[uid];
    } else {
      if (typeof status !== 'string') return false;
      if (map[uid] === status) return true;
      map[uid] = status;
    }
    return writeMap(map);
  }

  /** Rewrite many at once — what deleting a status in use needs, so that the
      whole cascade is ONE storage task and one ⌘Z takes it all back. `null` in
      the patch clears that record. */
  function mdRecordStatusSetMany(patch: Record<string, string | null>): boolean {
    if (!patch || typeof patch !== 'object') return false;
    const map = readMap();
    let moved = false;
    for (const uid of Object.keys(patch)) {
      if (!uid) continue;
      const want = patch[uid];
      if (want == null || want === '') {
        if (uid in map) { delete map[uid]; moved = true; }
      } else if (typeof want === 'string' && map[uid] !== want) {
        map[uid] = want; moved = true;
      }
    }
    return moved ? writeMap(map) : true;
  }

  /* ═══ sync ════════════════════════════════════════════════════════════════
     Absence of the KEY, not emptiness of the map: a device that has never
     moved a record has no document, and one that moved a record and moved it
     back has an empty one to say so. */

  function mdRecordStatusSyncDocs(): Record<string, unknown> {
    const out: Record<string, unknown> = {};
    if (g.mdRead(KEY, null) != null) out[DOC] = readMap();
    return out;
  }

  function mdRecordStatusApplyDoc(key: unknown, value: unknown): boolean {
    if (key !== DOC) return false;
    if (value == null) return g.mdRemove(KEY);
    if (typeof value !== 'object' || Array.isArray(value)) return false;
    const out: Record<string, string> = {};
    for (const uid of Object.keys(value as Record<string, unknown>)) {
      const word = (value as Record<string, unknown>)[uid];
      if (uid && typeof word === 'string' && word) out[uid] = word;
    }
    return writeMap(out);
  }

  Object.assign(g, {
    MD_RECORD_STATUS_KEY: KEY,
    mdRecordStatusAll,
    mdRecordStatusStored,
    mdRecordStatusSet,
    mdRecordStatusSetMany,
    mdRecordStatusSyncDocs,
    mdRecordStatusApplyDoc,
  });
})(typeof window !== 'undefined' ? window : globalThis);
